import { Track } from "livekit-client";
import type { RemoteParticipant, RemoteTrackPublication } from "livekit-client";

type ParticipantMediaState = {
    cameraEnabled: boolean;
    cameraPublication: RemoteTrackPublication | undefined;
    microphoneEnabled: boolean;
    microphonePublication: RemoteTrackPublication | undefined;
};

type LocalVideoTrack = MediaStreamTrack & {
    __meBusinessPlaceholder?: boolean;
};

type LocalMediaState = {
    cameraEnabled: boolean;
    microphoneEnabled: boolean;
};

const getFallbackPublication = (
    participant: RemoteParticipant,
    kind: Track.Kind,
    excludedSources: Track.Source[]
): RemoteTrackPublication | undefined => {
    return Array.from(participant.trackPublications.values()).find(publication => (
        publication.kind === kind && !excludedSources.includes(publication.source)
    ));
};

const publicationIsEnabled = (publication: RemoteTrackPublication | undefined): boolean => {
    return Boolean(publication) && publication?.isMuted !== true;
};

const localTrackIsEnabled = (track: MediaStreamTrack): boolean => {
    return track.enabled !== false && track.readyState !== "ended";
};

const localCameraTrackIsPublished = (track: MediaStreamTrack): boolean => {
    return !(track as LocalVideoTrack).__meBusinessPlaceholder && track.readyState !== "ended";
};

export const getLocalMediaState = (stream: MediaStream | null | undefined): LocalMediaState => {
    const cameraEnabled = stream?.getVideoTracks().some(localCameraTrackIsPublished) === true;
    const microphoneEnabled = stream?.getAudioTracks().some(localTrackIsEnabled) === true;

    return {
        cameraEnabled,
        microphoneEnabled
    };
};

export const getParticipantMediaState = (participant: RemoteParticipant): ParticipantMediaState => {
    const cameraPublication = participant.getTrackPublication(Track.Source.Camera) ||
        getFallbackPublication(participant, Track.Kind.Video, [Track.Source.ScreenShare]);
    const microphonePublication = participant.getTrackPublication(Track.Source.Microphone) ||
        getFallbackPublication(participant, Track.Kind.Audio, [Track.Source.ScreenShareAudio]);

    return {
        cameraEnabled: participant.isCameraEnabled || publicationIsEnabled(cameraPublication),
        cameraPublication,
        microphoneEnabled: participant.isMicrophoneEnabled || publicationIsEnabled(microphonePublication),
        microphonePublication
    };
};
